'use client';

import { useCallback, useEffect, useRef } from 'react';
import type { ChatRoute, ChatRouting, RoutablePanel } from '../routing';
import { messageElementId } from '../routing';

/**
 * Two-way binding between the address bar and chat's UI state.
 *
 * The whole thing is a no-op when `routing` is absent, which is how an embedded
 * widget behaves — see routing.ts for why the capability is injected rather
 * than assumed.
 *
 * THE LOOP PROBLEM
 *
 * State changes push a URL; a URL change applies state. Naively wired that is a
 * cycle: applying an inbound route sets state, which pushes a URL, which the
 * subscription reports as a change, which applies state again. `applyingRef`
 * marks the window during which state is being set FROM the URL, so the pusher
 * stays quiet for exactly that turn. This is why the two effects below are
 * separate and why neither is written as a single `useEffect` on everything.
 */

export interface RouteSyncOptions {
  /** Absent = URL syncing is off entirely (embedded widget). */
  routing?: ChatRouting;

  // ── current state, pushed OUT to the URL ──
  /** Active conversation, or null when there is none yet. */
  activeSessionId: string | null;
  /**
   * The active conversation is private (incognito).
   *
   * Private threads are deliberately kept OUT of the URL: their id is never
   * written to durable storage precisely so the thread leaves no trace, and an
   * address bar entry is a trace — it lands in browser history, in the back
   * button, and in whatever syncs those. So a private conversation shows as a
   * bare `/`.
   */
  isPrivate: boolean;
  /** Which panel is open, if any. */
  openPanel: RoutablePanel | null;

  // ── inbound, applied FROM the URL ──
  /** Switch to a conversation. Must be a no-op if the id is unknown. */
  onConversation(id: string): void;
  /** Open (or close, with null) a panel. */
  onPanel(panel: RoutablePanel | null): void;
  /** Preselect a persona named by the link. */
  onPersona?(personaId: string): void;
  /** Preselect a model named by the link. */
  onModel?(model: string): void;
  /** True once sessions have hydrated — an inbound id can't resolve before then. */
  isHydrated: boolean;
}

export function useRouteSync({
  routing,
  activeSessionId,
  isPrivate,
  openPanel,
  onConversation,
  onPanel,
  onPersona,
  onModel,
  isHydrated,
}: RouteSyncOptions): void {
  const applyingRef = useRef(false);
  /** The route we last pushed, so we don't push an identical one repeatedly. */
  const lastPushedRef = useRef<string | null>(null);
  /**
   * One-shot inbound fields.
   *
   * A conversation id keeps applying (back/forward must keep working), but
   * persona, model and the message anchor are opening instructions: they say
   * what a LINK wanted, not what the app should keep re-asserting. Re-applying
   * `?persona=` after the user picked a different one would make the URL
   * override their choice on every render.
   */
  const consumedRef = useRef<Set<string>>(new Set());

  // Latest callbacks, so the subscription below is established once rather than
  // resubscribing whenever a handler identity changes.
  const handlersRef = useRef({ onConversation, onPanel, onPersona, onModel });
  handlersRef.current = { onConversation, onPanel, onPersona, onModel };

  const apply = useCallback((route: ChatRoute) => {
    applyingRef.current = true;
    try {
      const h = handlersRef.current;
      if (route.conversationId) h.onConversation(route.conversationId);
      h.onPanel(route.panel ?? null);

      if (route.personaId && !consumedRef.current.has(`persona:${route.personaId}`)) {
        consumedRef.current.add(`persona:${route.personaId}`);
        h.onPersona?.(route.personaId);
      }
      if (route.model && !consumedRef.current.has(`model:${route.model}`)) {
        consumedRef.current.add(`model:${route.model}`);
        h.onModel?.(route.model);
      }
      if (route.messageId) scrollToMessage(route.messageId);
    } finally {
      // Released on a microtask, not synchronously: the state setters above are
      // async, so the push effect runs AFTER this call returns. Clearing the
      // flag here would let that run see it already false and echo the URL back.
      queueMicrotask(() => {
        applyingRef.current = false;
      });
    }
  }, []);

  // Inbound: the URL the app opened at, plus back/forward and pasted links.
  useEffect(() => {
    if (!routing) return;
    // Deferred until sessions exist — `onConversation` cannot resolve an id
    // against a list that has not loaded, so an early apply would silently drop
    // the conversation the link asked for.
    if (!isHydrated) return;
    apply(routing.read());
    return routing.subscribe(apply);
  }, [routing, isHydrated, apply]);

  // Outbound: reflect state changes back into the URL.
  useEffect(() => {
    if (!routing) return;
    if (applyingRef.current) return;
    // Gated on hydration for the same reason as the inbound effect, and it is
    // the more damaging of the two to get wrong.
    //
    // `activeSessionId` is null until sessions load, so without this the first
    // run pushes `conversationId: undefined` — the bare `/` — and because it is
    // the FIRST push it uses `replace`, overwriting the deep link in history
    // rather than adding an entry. The inbound effect then reads a URL whose
    // slug has already been destroyed, and a reload on a conversation silently
    // started a new one.
    if (!isHydrated) return;

    const next: ChatRoute = {
      // A private thread is addressed as `/` — see `isPrivate` above.
      conversationId: !isPrivate && activeSessionId ? activeSessionId : undefined,
      panel: openPanel ?? undefined,
    };
    const key = `${next.conversationId ?? ''}|${next.panel ?? ''}`;
    if (lastPushedRef.current === key) return;
    const isFirst = lastPushedRef.current === null;
    lastPushedRef.current = key;

    // `replace` on the first push so arriving at `/` and landing in the most
    // recent conversation does not leave a dead entry the back button returns
    // to. Later changes are real navigations and push.
    routing.navigate(next, { replace: isFirst });
  }, [routing, isHydrated, activeSessionId, isPrivate, openPanel]);
}

/**
 * Scroll a linked message into view and mark it briefly.
 *
 * Retried on a short interval because the element usually does not exist yet:
 * the conversation named by the same URL is still being switched to, and its
 * messages mount a tick or two later. Gives up rather than polling forever —
 * a link to a message that was deleted should quietly do nothing.
 */
function scrollToMessage(messageId: string): void {
  if (typeof document === 'undefined') return;
  const domId = messageElementId(messageId);
  let attempts = 0;

  const tick = () => {
    const el = document.getElementById(domId);
    if (!el) {
      if (++attempts > 40) return; // ~2s at 50ms
      setTimeout(tick, 50);
      return;
    }
    const reduced =
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    el.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'center' });
    // A ring rather than a background wash: the bubble already uses background
    // to say who is speaking, so tinting it would read as a change of role.
    el.classList.add('ring-2', 'ring-primary/60', 'rounded-md');
    setTimeout(() => {
      el.classList.remove('ring-2', 'ring-primary/60', 'rounded-md');
    }, 2000);
  };

  tick();
}
